import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import apiService from '../../services/api';

export const LOGIN_LOGO_MAX_BYTES = 200 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];
const ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.svg'];
const DEFAULT_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/login-default-logo.svg`;

const font = 'Metropolis, sans-serif';

// checkFile mirrors the backend's cheap checks so obvious mistakes never round-trip.
// Dimensions and SVG validity are checked by the backend.
function checkFile(file) {
  const name = file.name.toLowerCase();
  const typeOk = ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
  if (!typeOk) return 'Use a PNG, JPEG or SVG file.';
  if (file.size > LOGIN_LOGO_MAX_BYTES) return 'The file must be at most 200 KB.';
  return null;
}

function formatUpdated(info) {
  if (!info?.hasLogo) return null;
  const when = info.updatedAt ? new Date(info.updatedAt).toLocaleString() : '';
  return `Set by ${info.updatedBy || 'unknown'}${when ? ` on ${when}` : ''}`;
}

// LoginLogoSection is the login-page logo editor shown inside the Edit Customer
// dialog. It saves on its own (Save logo / Remove logo), independent of the
// dialog's "Update Customer" button.
export default function LoginLogoSection({ customer }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const inputRef = useRef(null);
  const customerId = customer?.id;

  useEffect(() => {
    // Start from a clean slate so nothing from a previously edited customer
    // (their logo, "Set by" line, Remove button, picked file) survives a switch.
    setInfo(null);
    setError(null);
    setFile(null);
    setConfirmRemove(false);
    if (!customerId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    apiService
      .getCustomerLoginLogo(customerId)
      .then((resp) => {
        if (!cancelled) setInfo(resp?.data || { hasLogo: false });
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load the current logo');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  // Object URL for the locally picked file, revoked when it changes or on unmount.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handlePick = (event) => {
    const picked = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!picked) return;
    const problem = checkFile(picked);
    if (problem) {
      setFile(null);
      setError(problem);
      return;
    }
    setError(null);
    setFile(picked);
  };

  const handleSave = async () => {
    if (!file) return;
    setSaving(true);
    setError(null);
    try {
      const resp = await apiService.uploadCustomerLoginLogo(customer.id, file);
      setInfo(resp?.data || null);
      setFile(null);
      toast.success(`Login logo updated for ${customer.name}`);
    } catch (err) {
      setError(err.message || 'Failed to save the logo');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    setError(null);
    try {
      const resp = await apiService.deleteCustomerLoginLogo(customer.id);
      setInfo(resp?.data || { hasLogo: false });
      setConfirmRemove(false);
      toast.success(`Login logo removed for ${customer.name}`);
    } catch (err) {
      setError(err.message || 'Failed to remove the logo');
    } finally {
      setSaving(false);
    }
  };

  if (!customer) return null;
  const previewSrc = previewUrl || (info?.hasLogo ? info.publicUrl : DEFAULT_LOGO_SRC);
  const updated = formatUpdated(info);

  return (
    <Box component="section" aria-labelledby="login-logo-heading">
      <Typography
        id="login-logo-heading"
        sx={{ fontFamily: font, fontWeight: 600, fontSize: '0.875rem', color: '#253E66', mb: 0.5 }}
      >
        Login Page Logo
      </Typography>
      <Typography sx={{ fontFamily: font, fontSize: '0.75rem', color: '#999999', mb: 1.5 }}>
        Replaces the CloudKeeper logo on this customer's sign-in page. Saved separately from the details above.
      </Typography>

      {/* Approximation of the Keycloak login card: same 180x48 logo box. */}
      <Box sx={{ background: '#F5F7FA', borderRadius: '8px', p: 2, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ background: '#FFFFFF', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', px: 3, py: 2.5, width: 320 }}>
          <Box sx={{ textAlign: 'center', mb: 1.5, minHeight: 48 }}>
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <img
                src={previewSrc}
                alt={`${customer.name} logo preview`}
                style={{ maxWidth: 180, maxHeight: 48, width: 'auto', height: 'auto', objectFit: 'contain' }}
              />
            )}
          </Box>
          <Typography sx={{ color: '#1A2B47', fontSize: 15, fontWeight: 500 }}>
            Login into CloudKeeper Prism for{' '}
            <Box component="span" sx={{ color: '#D6498E' }}>{customer.name}</Box>
          </Typography>
        </Box>
      </Box>

      {updated && !file && (
        <Typography variant="caption" sx={{ display: 'block', fontFamily: font, color: '#666666', mt: 1 }}>
          {updated}
        </Typography>
      )}

      <input
        ref={inputRef}
        data-testid="login-logo-input"
        type="file"
        accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
        hidden
        onChange={handlePick}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mt: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => inputRef.current && inputRef.current.click()}
          disabled={saving || loading}
          sx={{ fontFamily: font, textTransform: 'none', borderColor: '#4698D3', color: '#4698D3' }}
        >
          Choose file
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={!file || saving}
          aria-label="Save logo"
          sx={{ fontFamily: font, textTransform: 'none', backgroundColor: '#4698D3', '&:hover': { backgroundColor: '#3580B8' } }}
        >
          {saving ? <CircularProgress size={16} color="inherit" /> : 'Save logo'}
        </Button>
        {info?.hasLogo && !confirmRemove && (
          <Button
            size="small"
            color="error"
            onClick={() => setConfirmRemove(true)}
            disabled={saving}
            sx={{ fontFamily: font, textTransform: 'none', ml: 'auto' }}
          >
            Remove logo
          </Button>
        )}
      </Box>
      <Typography variant="caption" sx={{ display: 'block', fontFamily: font, color: '#666666', mt: 0.75 }}>
        {file
          ? `${file.name}: not saved yet. Click Save logo to apply it.`
          : 'PNG, JPEG or SVG, max 200 KB. 360×96 or larger recommended.'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}

      {confirmRemove && (
        <Alert
          severity="warning"
          sx={{ mt: 1.5 }}
          action={
            <>
              <Button color="inherit" size="small" onClick={() => setConfirmRemove(false)} disabled={saving}>
                Cancel
              </Button>
              <Button color="inherit" size="small" onClick={handleRemove} disabled={saving}>
                Yes, remove
              </Button>
            </>
          }
        >
          Remove the logo? The sign-in page goes back to the CloudKeeper logo.
        </Alert>
      )}
    </Box>
  );
}
